import React, { useContext, useCallback } from 'react';

import CountryFlag from 'components/base/CountryFlag/index';
import { TopTerritoriesItem } from './RadioSpinsTopTerritories';
import { EntityContext } from 'context/entityContext';
import { ICountryMetadata } from 'components/data-containers/KPIs/types';
import { IEntity } from 'types/shared';
import getShortName from 'helpers/getShortName';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { MAX_COUNTRY_NAME } from 'config';

/**
 * TopCountry displays a single TopCountry list item in the
 * RadioSpinsTopTerritories component.
 * @param {object} props React props
 * @param {object} props.item Country item
 * @param {('country'|'entity')} props.compareMode The current compare mode
 * @param {string} props.color hex code for the color
 * @param {number} props.rank This countries' rank
 */

interface ITopCountryProps {
  item: ICountryMetadata;
  compareMode: string;
  entity: IEntity;
  rank: number;
}

const TopCountry: React.FC<ITopCountryProps> = (props) => {
  const { item, compareMode, entity, rank } = props;

  const ctx = useContext(EntityContext);
  const { filters, countryIds, dataColors, entityIds } = ctx;
  const filteringCountry = filters?.country;

  const getFontWeight = useCallback(
    (countryId) => {
      if (compareMode) return 400;
      if (countryId === filteringCountry) return 600;
      return 400;
    },
    [compareMode, filteringCountry]
  );

  const getColor = useCallback(
    (countryId) => {
      if (compareMode === 'country') {
        const idx = countryIds?.findIndex((el) => countryId === el);
        return dataColors[idx as number];
      }

      if (compareMode === 'entity') {
        const idx = entityIds.findIndex((el) => entity.id === el);
        return dataColors[idx];
      }

      return;
    },
    [compareMode, countryIds, dataColors, entityIds, entity]
  );
  const countryDisplayName = getShortName(item.name);

  return (
    <TopTerritoriesItem
      data-rank={rank}
      fontWeight={getFontWeight(item.id)}
      dataColor={getColor(item.id)}
    >
      {compareMode ? (
        <>
          {item.metadata && <CountryFlag {...item.metadata} small />}
          {item.id}
        </>
      ) : (
        <>
          {item.metadata && <CountryFlag {...item.metadata} small />}
          <TooltipWrapper
            condition={item.name.length > MAX_COUNTRY_NAME}
            tooltipContent={item.name}
          >
            {countryDisplayName}
          </TooltipWrapper>
        </>
      )}
    </TopTerritoriesItem>
  );
};

export default TopCountry;
